if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.zoomImage = null;
        this.originalStyles = new Map();
        this.points = new Map();
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.lastTap = 0;

        this.handleResize = () => {
          if (this.zoomImage) this.resetZoom();
        };

        this.addEventListener('pointerdown', (event) => {
          if (!this.zoomImage || event.target !== this.zoomImage) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;

          this.points.set(event.pointerId, {
            x: event.clientX,
            y: event.clientY,
          });

          this.zoomImage.setPointerCapture(event.pointerId);
          this.beginGesture();
        });

        this.addEventListener('pointermove', (event) => {
          if (!this.points.has(event.pointerId)) return;

          event.preventDefault();
          this.points.set(event.pointerId, {
            x: event.clientX,
            y: event.clientY,
          });

          const points = [...this.points.values()];

          if (points.length >= 2 && this.gesture.distance > 0) {
            const distance = Math.hypot(
              points[1].x - points[0].x,
              points[1].y - points[0].y
            );

            const nextScale = Math.min(
              4,
              Math.max(1, this.gesture.scale * distance / this.gesture.distance)
            );

            const middleX = (points[0].x + points[1].x) / 2;
            const middleY = (points[0].y + points[1].y) / 2;
            const ratio = nextScale / this.gesture.scale;

            this.x =
              middleX - this.gesture.centerX -
              (this.gesture.middleX - this.gesture.centerX - this.gesture.x) * ratio;

            this.y =
              middleY - this.gesture.centerY -
              (this.gesture.middleY - this.gesture.centerY - this.gesture.y) * ratio;

            this.scale = nextScale;
          } else if (points.length === 1 && this.scale > 1) {
            this.x = this.gesture.x + points[0].x - this.gesture.startX;
            this.y = this.gesture.y + points[0].y - this.gesture.startY;
          }

          this.renderZoom();
        }, { passive: false });

        const endPointer = (event) => {
          if (!this.points.has(event.pointerId)) return;

          const point = this.points.get(event.pointerId);
          const isTap =
            event.type === 'pointerup' &&
            this.points.size === 1 &&
            !this.gesture.multi &&
            Math.hypot(
              point.x - this.gesture.startX,
              point.y - this.gesture.startY
            ) < 10;

          this.points.delete(event.pointerId);

          if (isTap) {
            const now = Date.now();

            if (now - this.lastTap < 300) {
              this.scale = this.scale > 1 ? 1 : 2;
              this.x = 0;
              this.y = 0;
              this.lastTap = 0;
              this.renderZoom();
            } else {
              this.lastTap = now;
            }
          } else {
            this.lastTap = 0;
          }

          if (this.points.size) this.beginGesture();
        };

        this.addEventListener('pointerup', endPointer);
        this.addEventListener('pointercancel', endPointer);
        this.addEventListener('lostpointercapture', endPointer);

        this.addEventListener('dragstart', (event) => {
          if (event.target === this.zoomImage) event.preventDefault();
        });
      }

      rememberStyle(element) {
        if (element && !this.originalStyles.has(element)) {
          this.originalStyles.set(element, element.getAttribute('style'));
        }
      }

      restoreMedia() {
        this.points.clear();

        this.originalStyles.forEach((style, element) => {
          if (style === null) {
            element.removeAttribute('style');
          } else {
            element.setAttribute('style', style);
          }
        });

        this.originalStyles.clear();
        this.zoomImage = null;
        this.lastTap = 0;
        window.removeEventListener('resize', this.handleResize);
      }

      hide() {
        super.hide();
        this.restoreMedia();
      }

      disconnectedCallback() {
        this.restoreMedia();
        if (super.disconnectedCallback) super.disconnectedCallback();
      }

      show(opener) {
        this.restoreMedia();
        super.show(opener);
        this.showActiveMedia();
      }

      beginGesture() {
        const points = [...this.points.values()];
        if (!points.length || !this.zoomImage) return;

        const rect = this.zoomImage.getBoundingClientRect();

        this.gesture = {
          x: this.x,
          y: this.y,
          scale: this.scale,
          startX: points[0].x,
          startY: points[0].y,
          multi: points.length > 1,
          distance: points.length > 1
            ? Math.hypot(
                points[1].x - points[0].x,
                points[1].y - points[0].y
              )
            : 0,
          middleX: points.length > 1
            ? (points[0].x + points[1].x) / 2
            : points[0].x,
          middleY: points.length > 1
            ? (points[0].y + points[1].y) / 2
            : points[0].y,
          centerX: rect.left + rect.width / 2 - this.x,
          centerY: rect.top + rect.height / 2 - this.y,
        };
      }

      resetZoom() {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.points.clear();
        this.renderZoom();
      }

      renderZoom() {
        if (!this.zoomImage) return;

        const limitX = this.zoomImage.offsetWidth * (this.scale - 1) / 2;
        const limitY = this.zoomImage.offsetHeight * (this.scale - 1) / 2;

        this.x = Math.max(-limitX, Math.min(limitX, this.x));
        this.y = Math.max(-limitY, Math.min(limitY, this.y));

        this.zoomImage.style.setProperty(
          'transform',
          `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`,
          'important'
        );
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const mediaItems = [...this.querySelectorAll('[data-media-id]')];
        const activeMedia = mediaItems.find(
          (element) => element.getAttribute('data-media-id') === mediaId
        );

        if (!activeMedia) return;

        mediaItems.forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });

        const template = activeMedia.querySelector('template');
        const content = template ? template.content : null;
        const container = this.querySelector('[role="document"]');

        const isMobileImage =
          window.matchMedia('(max-width: 749px)').matches &&
          activeMedia.tagName === 'IMG';

        if (isMobileImage && container) {
          // Only apply the mobile layout to image media.
          mediaItems.forEach((element) => {
            this.rememberStyle(element);

            if (element !== activeMedia) {
              element.style.setProperty('display', 'none', 'important');
            }
          });

          this.rememberStyle(container);
          this.rememberStyle(activeMedia);

          const setStyles = (element, styles) => {
            Object.entries(styles).forEach(([property, value]) => {
              element.style.setProperty(property, value, 'important');
            });
          };

          setStyles(container, {
            display: 'flex',
            'align-items': 'center',
            'justify-content': 'center',
            width: '100%',
            height: '100dvh',
            'max-height': '100dvh',
            'min-height': '0',
            padding: '56px 12px 24px',
            margin: '0',
            overflow: 'hidden',
            'box-sizing': 'border-box',
          });

          setStyles(activeMedia, {
            display: 'block',
            position: 'relative',
            width: 'auto',
            height: 'auto',
            'min-width': '0',
            'min-height': '0',
            'max-width': '100%',
            'max-height': '100%',
            'object-fit': 'contain',
            margin: '0',
            'flex-shrink': '1',
            'touch-action': 'none',
            'user-select': 'none',
            '-webkit-user-select': 'none',
            'transform-origin': 'center center',
            transition: 'none',
          });

          container.scrollLeft = 0;
          container.scrollTop = 0;

          this.zoomImage = activeMedia;
          this.resetZoom();
          window.addEventListener('resize', this.handleResize);
        } else {
          activeMedia.scrollIntoView();

          if (container) {
            container.scrollLeft =
              (activeMedia.clientWidth - container.clientWidth) / 2;
          }
        }

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          content &&
          content.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }
    }
  );
}